import {
  FIRST_DAY,
  loadHistory,
  monthCells,
  shiftMonth,
  startedDays,
  tally,
  today,
  type BirdleDay,
} from '../../core/birdle';
import { el, ensureStyle } from '../dom';
import { ensureQuizStyle, backBtn } from '../components/quiz-chrome';
import { icon } from '../components/icons';
import type { View } from '../router';
import { t } from '../../strings';
import { monthYear, readableDay, weekdayInitial } from '../../strings/format';

// The Birdle calendar: a month to a screen, a day to a cell.
//
// It is the record `core/birdle.ts` keeps, drawn — the number in a cell is the
// turns that day took — and it is also the way in to any day that was missed,
// since every cell from the first Birdle to today opens that day's board.
//
// The one thing it says that the record's numbers alone do not is WHEN a day
// was finished. A day played on the day has a heavy border and one played
// afterwards a hairline, because those are different things to have done: the
// first is the daily, the second is the archive.
//
// Reached from the button beside Birdle on Home, from the header of the board,
// and from the done panel. Under `.quiz` for the header row only.

ensureQuizStyle();

ensureStyle(
  'birdle-calendar-css',
  `
  .quiz.birdlecal { gap:16px; }
  .birdlecal .monthbar { display:flex; align-items:center; gap:6px; }
  .birdlecal .monthbar h1 { flex:1; margin:0; text-align:center; font-size:25px; }
  .birdlecal .monthbar .btn { color:var(--ink-soft); }
  /* Still there at either end of the range, so the month's name does not jump
     sideways on the first and last page. */
  .birdlecal .monthbar .btn:disabled { opacity:.22; }

  .birdlecal .weeks { display:grid; grid-template-columns:repeat(7, 1fr); gap:6px; }
  .birdlecal .wd { text-align:center; font-size:9px; padding-bottom:1px; }

  /* A cell. The date rides the corner and the score takes the middle, which is
     the reverse of a wall calendar and the right way round here: every cell has
     a date, and the score is the thing being looked for.

     The borders are inset shadows rather than borders, so a heavy one and a
     hairline occupy the same box and the grid does not shift by a pixel between
     a day played on time and one that was not. */
  .birdlecal .day { position:relative; aspect-ratio:1; display:flex; align-items:center;
    justify-content:center; border-radius:var(--radius); color:var(--ink);
    text-decoration:none; font-family:var(--font-display); font-size:19px;
    font-variant-numeric:tabular-nums; line-height:1; padding-top:8px; }
  /* Inside the heavy border with a little to spare, and the score is set low
     (the padding above) so a two-digit date never touches it. */
  .birdlecal .day .n { position:absolute; top:5.5px; left:7px; font-family:var(--font-mono);
    font-size:9px; line-height:1; color:var(--ink-soft); }
  /* Not played, and playable: an empty slot, dashed like the stitched cards. */
  .birdlecal .day.open { box-shadow:none; border:1.5px dashed var(--hairline-strong); }
  .birdlecal .day.open .n { top:4px; left:5.5px; }
  .birdlecal .day.open .score { color:var(--ink-soft); }
  /* Before the first Birdle, and after today. */
  .birdlecal .day.out { opacity:.32; }
  .birdlecal .day.won { background:var(--moss-pale); color:var(--on-moss-pale);
    --edge:var(--moss); }
  .birdlecal .day.lost { background:var(--sunk); color:var(--ink-soft);
    --edge:var(--ink-soft); }
  .birdlecal .day.won, .birdlecal .day.lost { box-shadow:inset 0 0 0 1px var(--edge); }
  /* Finished on the day itself. In ink whichever way the day went, because ink
     is the one colour that is the far end of the scale from the page in both
     themes: the deep moss this first used is the darkest green on linen and
     very nearly the cell's own fill on loam, where the heavy border vanished. */
  .birdlecal .day.onday { --edge:var(--ink); box-shadow:inset 0 0 0 3.5px var(--edge); }
  /* Today wears the accent on its date, whatever state the day is in. */
  .birdlecal .day.today .n { background:var(--accent); color:var(--on-accent);
    border-radius:999px; padding:1px 4px; top:3px; left:3px; }
  .birdlecal a.day:focus-visible { outline:2px solid var(--accent); outline-offset:2px; }

  /* What the two borders mean, drawn with the borders themselves. */
  .birdlecal .legend { display:flex; flex-wrap:wrap; gap:6px 16px; justify-content:center;
    font-size:11.5px; color:var(--ink-soft); }
  .birdlecal .legend span { display:inline-flex; align-items:center; gap:6px; }
  .birdlecal .legend .day { width:17px; aspect-ratio:1; border-radius:5px; flex:none; }
  .birdlecal .legend .day.onday { box-shadow:inset 0 0 0 3px var(--edge); }

  /* The record, added up. */
  .birdlecal .tally { display:flex; flex-direction:column; gap:12px; }
  .birdlecal .tally .nums { display:grid; grid-template-columns:repeat(3, 1fr); gap:7px; }
  .birdlecal .tally .num { display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:8px 4px 7px; background:var(--paper-2); border-radius:var(--radius); }
  .birdlecal .tally .num .v { font-family:var(--font-display); font-weight:600; font-size:24px;
    line-height:1.05; font-variant-numeric:tabular-nums; }
  .birdlecal .tally .num .label { font-size:8.6px; }
  /* One bar per turn count, and one for the days that ran out. The count sits
     on the end of its bar, so a bar of nothing is still a row with a nought on
     it rather than a gap. */
  .birdlecal .dist { display:grid; grid-template-columns:auto 1fr; gap:5px 9px;
    align-items:center; font-family:var(--font-mono); font-size:11.5px;
    color:var(--ink-soft); }
  .birdlecal .dist .bar { display:flex; align-items:center; gap:7px; }
  .birdlecal .dist .bar i { display:block; height:13px; min-width:2px; border-radius:3px;
    background:var(--moss); }
  .birdlecal .dist .bar.lost i { background:var(--ink-soft); }
`,
);

export const birdleCalendarView: View = async (root, ctx) => {
  const { day: thisDay, pool } = today();
  const [history, started] = await Promise.all([loadHistory(), startedDays(pool)]);
  const byDay = new Map<string, BirdleDay>(history.map((d) => [d.day, d]));

  const firstMonth = FIRST_DAY.slice(0, 7);
  const lastMonth = thisDay.slice(0, 7);
  // The month asked for, when it is one this calendar has; this month otherwise.
  const asked = ctx.params.month ?? '';
  let month =
    /^\d{4}-\d{2}$/.test(asked) && asked >= firstMonth && asked <= lastMonth ? asked : lastMonth;

  const wrap = el('div.quiz.birdlecal', {});
  root.append(wrap);
  draw();

  function turn(to: string): void {
    month = to;
    // The address follows the page, so a board opened from here comes back to
    // the month it was opened from — without a history entry per month.
    ctx.rewrite(`/birdle/calendar?month=${month}`);
    draw();
  }

  function draw(): void {
    const stepper = (dir: -1 | 1): HTMLElement => {
      const to = shiftMonth(month, dir);
      const label = dir < 0 ? t('birdle.calendar.previous') : t('birdle.calendar.next');
      return el(
        'button.btn.btn-plain',
        {
          type: 'button',
          title: label,
          'aria-label': label,
          disabled: to < firstMonth || to > lastMonth,
          onclick: () => turn(to),
        },
        icon(dir < 0 ? 'chevron-left' : 'chevron-right', { size: 22 }),
      );
    };

    const cells = monthCells(month);
    wrap.replaceChildren(
      el(
        'div.qhead',
        {},
        el('div.bar', {}, backBtn(ctx), el('div.prompt.label', {}, t('birdle.calendar.title'))),
      ),
      el('div.monthbar', {}, stepper(-1), el('h1', {}, monthYear(month)), stepper(1)),
      el(
        'div.weeks',
        {},
        // The first whole week on the page names the columns, whichever month
        // it is — every row of a month's grid starts on the same weekday.
        ...weekdays(cells).map((d) => el('div.wd.label', {}, d)),
        ...cells.map(cell),
      ),
      legend(),
      summary(),
    );
  }

  function cell(day: string | null): HTMLElement {
    if (!day) return el('div.day.pad', {});
    const n = el('span.n', {}, String(Number(day.slice(8))));
    if (day < FIRST_DAY || day > thisDay) return el('div.day.out', {}, n);

    const rec = byDay.get(day);
    const date = readableDay(day);
    const when = rec?.late ? 'late' : 'onDay';
    const [state, score, label] = rec
      ? rec.won
        ? ['won', String(rec.turns), t('birdle.calendar.day.won', { date, turns: rec.turns, when })]
        : ['lost', t('birdle.calendar.lost'), t('birdle.calendar.day.lost', { date, when })]
      : started.has(day)
        ? ['open', t('birdle.calendar.started'), t('birdle.calendar.day.started', { date })]
        : day === thisDay
          ? ['open', '', t('birdle.calendar.day.today', { date })]
          : ['open', '', t('birdle.calendar.day.open', { date })];

    const path = day === thisDay ? '/birdle' : `/birdle?day=${day}`;
    const link = el(
      `a.day.${state}`,
      { href: `#${path}`, title: label, 'aria-label': label },
      n,
      el('span.score', {}, score),
    );
    if (rec && !rec.late) link.classList.add('onday');
    if (day === thisDay) link.classList.add('today');
    link.addEventListener('click', (e) => {
      e.preventDefault();
      ctx.navigate(path);
    });
    return link;
  }

  function legend(): HTMLElement {
    const key = (classes: string, text: string): HTMLElement =>
      el('span', {}, el(`i.day.${classes}`, {}), text);
    return el(
      'div.legend',
      {},
      key('won.onday', t('birdle.calendar.legend.onDay')),
      key('won', t('birdle.calendar.legend.late')),
      key('open', t('birdle.calendar.legend.open')),
    );
  }

  /** The whole record, not the month's: a month is a page, not a period. */
  function summary(): HTMLElement {
    const sum = tally(history);
    if (!sum.played) {
      return el(
        'p.meta',
        { style: { textAlign: 'center', margin: '0' } },
        t('birdle.calendar.tally.none', { first: readableDay(FIRST_DAY) }),
      );
    }
    const num = (value: number, label: string): HTMLElement =>
      el('div.num', {}, el('span.v', {}, String(value)), el('span.label', {}, label));

    const most = Math.max(1, sum.lost, ...sum.byTurns);
    const bar = (name: string, count: number, lost = false): HTMLElement[] => [
      el('span', {}, name),
      el(
        `div.bar${lost ? '.lost' : ''}`,
        { title: t('birdle.calendar.tally.row', { turns: name, n: count }) },
        el('i', { style: { width: `${(count / most) * 86}%` } }),
        String(count),
      ),
    ];

    return el(
      'section.tally.card',
      {},
      el('span.label', {}, t('birdle.calendar.tally.label')),
      el(
        'div.nums',
        {},
        num(sum.played, t('birdle.calendar.tally.played')),
        num(sum.won, t('birdle.calendar.tally.solved')),
        num(sum.onDay, t('birdle.calendar.tally.onDay')),
      ),
      el(
        'div.dist',
        { 'aria-label': t('birdle.calendar.tally.turns') },
        ...sum.byTurns.flatMap((count, i) => bar(String(i + 1), count)),
        ...bar(t('birdle.calendar.lost'), sum.lost, true),
      ),
    );
  }
};

/** The seven weekday initials, read off the first full week on the page. */
function weekdays(cells: (string | null)[]): string[] {
  for (let i = 0; i + 7 <= cells.length; i += 7) {
    const week = cells.slice(i, i + 7);
    if (week.every((d): d is string => !!d)) return week.map((d) => weekdayInitial(d));
  }
  return [];
}
